import React, { useEffect, useMemo, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { autocompletion } from '@codemirror/autocomplete';
import { linter } from '@codemirror/lint';
import { css } from '@codemirror/lang-css';
import debounce from 'lodash/debounce';
import pt from 'prop-types';

import { renderMarkdown } from 'utils/string';

import Label from 'components/form/label';
import { getCanvasElement } from 'components/storefront/editor/utils/easyblocks';

import withWidget from './with-widget';

const DEBOUNCE_DELAY = 700;
const CHECK_SECTION_ELEMENT_INTERVAL = 500;
const CHECK_SECTION_ELEMENT_TIMEOUT = 10000;
const PAGE_SECTION_SELECTOR = 'main section .swell-page-section';

const EXCLUSIONS = Object.freeze(['.fr-box']);

/** @param {HTMLElement} element */
function isExclusion(element) {
  return EXCLUSIONS.some(
    (exclusion) => element.matches(exclusion) || element.closest(exclusion),
  );
}

function cssAutocomplete(metadata) {
  return autocompletion({
    override: [
      (context) => {
        const word = context.matchBefore(/\.\w*|#\w*/);
        if (!word) return null;

        const options = [];
        if (word.text.startsWith('.')) {
          options.push(
            ...Array.from(metadata.classes).map((cls) => ({
              label: `.${cls}`,
            })),
          );
        }
        if (word.text.startsWith('#')) {
          options.push(
            ...Array.from(metadata.ids).map((id) => ({ label: `#${id}` })),
          );
        }

        return {
          from: word.from,
          options,
        };
      },
    ],
  });
}

function cssLinter(metadata) {
  return linter((view) => {
    const diagnostics = [];
    const content = view.state.doc.toString();

    const regex = /\.(\w[\w-]*)|#(\w[\w-]*)/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      const [, className, id] = match;
      if (className && !metadata.classes.has(className)) {
        diagnostics.push({
          from: match.index,
          to: match.index + className.length + 1,
          message: `Unknown class: ${className}`,
          severity: 'warning',
        });
      }
      if (id && !metadata.ids.has(id)) {
        diagnostics.push({
          from: match.index,
          to: match.index + id.length + 1,
          message: `Unknown ID: ${id}`,
          severity: 'warning',
        });
      }
    }
    return diagnostics;
  });
}

/** @param {HTMLElement} element */
function extractCSSMetadata(element) {
  const metadata = {
    classes: new Set(),
    ids: new Set(),
  };

  element.querySelectorAll('[class]').forEach((el) => {
    if (typeof el.className !== 'string' || isExclusion(el)) {
      return;
    }

    el.className.split(/\s+/).forEach((cls) => metadata.classes.add(cls));
  });

  element.querySelectorAll('[id]').forEach((el) => {
    metadata.ids.add(el.id);
  });

  return metadata;
}

/**
 * @param {string} css
 * @returns {string[]}
 */
function splitCssToBlocks(css) {
  const blocks = [];

  for (let pos = 0; pos !== -1; ) {
    const delimiter = css.indexOf('}', pos);

    if (delimiter === -1) {
      const last = css.slice(pos).trim();

      if (last) {
        blocks.push(last);
      }

      break;
    }

    blocks.push(css.slice(pos, delimiter + 1).trim());
    pos = delimiter + 1;
  }

  return blocks;
}

const debounced = debounce(
  /**
   * @param {string} value
   * @param {(value: string) => void} onChange
   * @param {AbortController} controller
   */
  (value, onChange, controller) => {
    if (!controller.signal.aborted) {
      onChange(null, splitCssToBlocks(value));
    }
  },
  DEBOUNCE_DELAY,
);

function SwellCSS({ value, onChange, field }) {
  const initialValue = useMemo(() => {
    return Array.isArray(value) ? value.join('\n\n') : value;
  }, [value]);

  const editorValueRef = useRef(initialValue);

  const [sectionElement, setSectionElement] = React.useState(null);
  const [cssMetadata, setCSSMetadata] = React.useState(null);

  const controller = useMemo(() => new AbortController(), []);

  useEffect(() => {
    return function cleanup() {
      controller.abort();
    };
  }, [controller]);

  const onChangeMirror = React.useCallback(
    (value, viewUpdate) => {
      editorValueRef.current = value;
      debounced(value, onChange, controller);
    },
    [onChange, controller],
  );

  const contentSectionKey = useMemo(
    () => Number(field.name.split('.')[3]),
    [field.name],
  );

  useEffect(() => {
    if (sectionElement) {
      return;
    }

    // Searching for the section element using setInterval
    // because the CSS widget may render before the section appears in the preview.
    let timeout = CHECK_SECTION_ELEMENT_TIMEOUT;

    let intervalId = setInterval(() => {
      timeout -= CHECK_SECTION_ELEMENT_INTERVAL;

      // Stop searching if the element is not found within the timeout.
      if (timeout <= 0) {
        clearInterval(intervalId);
        intervalId = 0;
        return;
      }

      const canvasElement = getCanvasElement()?.contentWindow.document.body;

      if (!canvasElement) {
        return;
      }

      const element = canvasElement.querySelectorAll(PAGE_SECTION_SELECTOR)[
        contentSectionKey
      ];

      if (element) {
        setSectionElement(element);
        setCSSMetadata(extractCSSMetadata(element));
        clearInterval(intervalId);
        intervalId = 0;
      }
    }, CHECK_SECTION_ELEMENT_INTERVAL);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [contentSectionKey, sectionElement]);

  const extensions = useMemo(() => {
    const baseExtensions = [css()];
    if (cssMetadata) {
      baseExtensions.push(cssLinter(cssMetadata), cssAutocomplete(cssMetadata));
    }
    return baseExtensions;
  }, [cssMetadata]);

  return (
    <div className="form-field">
      <Label label={field.label} htmlFor="css-editor" />

      <CodeMirror
        id="css-editor"
        height="200px"
        extensions={extensions}
        value={initialValue}
        onChange={onChangeMirror}
      />

      {field.description && (
        <span className="form-field-hint">
          {typeof hint === 'string'
            ? renderMarkdown(field.description)
            : field.description}
        </span>
      )}
    </div>
  );
}

SwellCSS.propTypes = {
  value: pt.oneOfType([pt.string, pt.arrayOf(pt.string)]),

  field: pt.shape({
    name: pt.string,
    label: pt.string,
    description: pt.string,
  }).isRequired,

  onChange: pt.func.isRequired,
};

export default React.memo(withWidget(SwellCSS));
